import styled, { css } from 'styled-components';

import { Frame } from './StyledTemplates';

type Direction = 'top' | 'right' | 'bottom' | 'left';

type TooltipProps = {
    children?: React.ReactNode;
    label?: string;
    side?: Direction;
    onMouseEnter?: () => void;
};

export default function Tooltip(props: TooltipProps) {
    const { children, label, side = 'top', onMouseEnter = () => {} } = props;
    return (
        <TooltipCard side={side} onMouseEnter={onMouseEnter}>
            <TooltipText>{children}</TooltipText>
            <TooltipBox side={side}>{label || ''}</TooltipBox>
        </TooltipCard>
    );
}

const TooltipText = styled.div`
    cursor: pointer;
    width: max-content;
`;

const TooltipBox = styled(Frame)<{ side?: Direction }>`
    background-color: ${({ theme }) => theme.tooltip};
    padding: 1rem;
    max-width: 250px;
    border-radius: 0.5rem;
    color: #fff;
    visibility: hidden;
    opacity: 0;
    position: absolute;
    font-weight: normal;
    font-size: 0.8rem;
    line-height: 1rem;
    white-space: nowrap;
    min-width: fit-content;
    width: fit-content;

    ${({ side = 'top' }) =>
        ({
            top: css`
                top: -11px;
                left: 50%;
                transform: translate(-50%, calc(-100% - 3px));
            `,
            right: css`
                left: 100%;
                top: 50%;
                transform: translate(0, -50%);
            `,
            bottom: css`
                bottom: -11px;
                left: 50%;
                transform: translate(-50%, calc(100% + 3px));
            `,
            left: css`
                right: 100%;
                top: 50%;
                transform: translate(0, -50%);
            `,
        }[side])}

    * {
        color: #ffffff;
    }

    &:after {
        content: '';
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 6px solid ${({ theme }) => theme.tooltip};
        position: absolute;
        ${({ side = 'top' }) =>
            ({
                top: css`
                    top: 100%;
                    transform: rotate(-180deg) translate(0, 0px);
                `,
                right: css`
                    right: 100%;
                    transform: rotate(-90deg) translate(0, 2.5px);
                `,
                bottom: css`
                    bottom: 100%;
                    transform: rotate(0deg) translate(0, 0.5px);
                `,
                left: css`
                    left: 100%;
                    transform: rotate(90deg) translate(0, 2.5px);
                `,
            }[side])}
    }
`;

const TooltipCard = styled.div<{ side?: string }>`
    position: relative;
    z-index: 2;
    & ${TooltipText}:hover + ${TooltipBox} {
        visibility: visible;
        opacity: 1;
        ${({ side = 'top' }) =>
            ({
                top: css`
                    top: -11px;
                    left: 50%;
                    transform: translate(-50%, calc(-100% - 0px));
                `,
                right: css`
                    left: 100%;
                    top: 50%;
                    transform: translate(-3px, -50%);
                `,
                bottom: css`
                    bottom: -11px;
                    left: 50%;
                    transform: translate(-50%, calc(100% + 0px));
                `,
                left: css`
                    right: 100%;
                    top: 50%;
                    transform: translate(3px, -50%);
                `,
            }[side])}
    }
`;

type TooltipWrapperProps = TooltipProps & {
    disabled: boolean;
};

export function TooltipWrapper(props: TooltipWrapperProps) {
    const { disabled, ...rest } = props;
    return disabled ? <>{rest.children}</> : <Tooltip {...rest} />;
}
